import { pageTitle } from '@palaccount/config';
import {
  MFG_MESSAGES,
  type ConsumptionVoucherResponse,
  type IssueType,
} from '@palaccount/contracts';
import { Info, Lock, Plus, Undo2, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Amount, Ltr, Pct, Qty } from '@/components/amount';
import { Gated } from '@/components/permission-gate';
import { Pager } from '@/components/pager';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { usePagedState, usePageRange } from '@/lib/paging';
import { localizeServerMessage, refusalMessage } from '@/lib/server-messages';
import { cn } from '@/lib/utils';
import { useCompanyToday } from '@/lib/session';

import { Typeahead } from '@/components/ui/typeahead';
import { ItemCell } from '../inventory/item-cell';
import { useItemPicker } from '../inventory/item-picker';
import { Modal } from '../inventory/modal';
import { useWarehouses } from '../inventory/stock-api';
import {
  useConsumptionLedger,
  useConsumptionVariance,
  usePostVoucher,
  useUsageReport,
  type LedgerFilters,
} from './api';
import { ManufacturingTabs } from './manufacturing-tabs';
import { ledgerQty, varianceBandClass } from './mfg-format';
import { useWorkOrderPicker } from './work-order-picker';

const ISSUE_TYPES: readonly IssueType[] = ['planned', 'extra', 'return_to_stock', 'indirect'];

/**
 * Setting a filter, or CLEARING it by removing the key rather than setting it
 * to undefined — which matters because BR-MFG-04-24 makes "no filter" a
 * meaningful state: an absent parameter means all four issue types, so the
 * ledger a user sees is never narrower than the one they wrote into.
 */
function withFilter(filters: LedgerFilters, key: keyof LedgerFilters, value: string): LedgerFilters {
  const next: LedgerFilters = { ...filters };
  if (value === '') delete next[key];
  else next[key] = value as IssueType & string;
  return next;
}

/**
 * MFG-04 — MATERIAL CONSUMPTION.
 *
 * "The word that matters most on this screen is IMMUTABLE", and the screen
 * prints it: "Posted transactions are immutable — corrections create a new
 * linked entry."
 *
 * There is no edit control and no delete control anywhere below, "for any
 * role, including Administrators, and none for the Owner either" (§6). The
 * single row action is Correct, which opens the SAME form in correction mode.
 *
 * *Changed by owner ruling R1: the prototype drew a second, cut-down list
 * beside this one — four columns, no money, no tabs, no Correct button, and
 * silently filtered to issue type Planned, so a user could post an Extra issue
 * and then be unable to find or fix it. There is one ledger view.*
 */
export function ConsumptionPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'ledger' | 'variance' | 'usage'>('ledger');

  useEffect(() => {
    document.title = pageTitle(t('mfg.consumption'));
  }, [t]);

  return (
    <div className="grid gap-4">
      <ManufacturingTabs />
      <h2 className="text-lg font-bold">{t('mfg.consumption')}</h2>
      <nav className="flex flex-wrap gap-1 border-b pb-px" aria-label={t('mfg.consumption')}>
        {(['ledger', 'variance', 'usage'] as const).map((entry) => (
          <button
            key={entry}
            type="button"
            onClick={() => setTab(entry)}
            className={cn(
              'rounded-t-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground',
              tab === entry && 'border-b-2 border-primary text-foreground',
            )}
          >
            {t(`mfg.mcTab.${entry}`)}
          </button>
        ))}
      </nav>
      {tab === 'ledger' ? <LedgerTab /> : null}
      {tab === 'variance' ? <VarianceTab /> : null}
      {tab === 'usage' ? <UsageTab /> : null}
    </div>
  );
}

function LedgerTab() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<LedgerFilters>({});
  /** NFR-42 — the page rides in the URL, and every filter resets it. */
  const paging = usePagedState();
  const { data, isPending, isError } = useConsumptionLedger(filters, paging.page, paging.pageSize);
  const range = usePageRange(data, paging.pageSize);
  // GEN-50 — §5.2's two pickers, searched rather than holding the whole list.
  const workOrderPicker = useWorkOrderPicker(filters.workOrderId);
  const itemPicker = useItemPicker([filters.itemId]);
  const { data: warehouses } = useWarehouses();
  const [form, setForm] = useState<{ correcting: ConsumptionVoucherResponse | null } | null>(null);

  return (
    <div className="grid gap-4">
      <Card>
        <CardContent className="grid gap-3 py-4">
          {/* §5.2's six filters. BR-MFG-04-24: an absent filter means ALL FOUR
              issue types — narrowing is a choice the user makes and can undo,
              never one applied behind their back. */}
          <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
            <Typeahead
              label={t('mfg.filterWorkOrder')}
              value={filters.workOrderId ?? null}
              onChange={(id) =>
                paging.change(setFilters)(withFilter(filters, 'workOrderId', id ?? ''))
              }
              options={workOrderPicker.options}
              selected={workOrderPicker.optionFor(filters.workOrderId)}
              query={workOrderPicker.query}
              onQueryChange={workOrderPicker.setQuery}
              clearLabel={t('mfg.allWorkOrders')}
              placeholder={t('mfg.allWorkOrders')}
              emptyLabel={t('picker.noMatchingWorkOrder')}
              loading={workOrderPicker.loading}
              size="cell"
            />
            <Typeahead
              label={t('mfg.filterItem')}
              value={filters.itemId ?? null}
              onChange={(id) => paging.change(setFilters)(withFilter(filters, 'itemId', id ?? ''))}
              options={itemPicker.options}
              selected={itemPicker.optionFor(filters.itemId)}
              query={itemPicker.query}
              onQueryChange={itemPicker.setQuery}
              clearLabel={t('mfg.allItems')}
              placeholder={t('mfg.allItems')}
              emptyLabel={t('picker.noMatchingItem')}
              loading={itemPicker.loading}
              size="cell"
            />
            <NativeSelect
              aria-label={t('mfg.filterType')}
              value={filters.issueType ?? ''}
              onChange={(event) => paging.change(setFilters)(withFilter(filters, 'issueType', event.target.value))}
            >
              <option value="">{t('mfg.allTypes')}</option>
              {ISSUE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`mfg.issueType.${type}`)}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label={t('mfg.filterWarehouse')}
              value={filters.warehouseId ?? ''}
              onChange={(event) => paging.change(setFilters)(withFilter(filters, 'warehouseId', event.target.value))}
            >
              <option value="">{t('mfg.allWarehouses')}</option>
              {(warehouses ?? []).map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </option>
              ))}
            </NativeSelect>
            <Input
              aria-label={t('common.from')}
              type="date"
              value={filters.from ?? ''}
              onChange={(event) => paging.change(setFilters)(withFilter(filters, 'from', event.target.value))}
            />
            <Input
              aria-label={t('common.to')}
              type="date"
              value={filters.to ?? ''}
              onChange={(event) => paging.change(setFilters)(withFilter(filters, 'to', event.target.value))}
            />
          </div>
          <Gated permission="manufacturing.create" reasonKey="perm.cannot.postConsumption">
            <div>
              <Button onClick={() => setForm({ correcting: null })}>
                <Plus aria-hidden /> {t('mfg.newVoucher')}
              </Button>
            </div>
          </Gated>
        </CardContent>
      </Card>

      {/* §5.3 — the immutability strip, with the filtered total on its right. */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/40 p-3">
        <Lock className="size-4 text-muted-foreground" aria-hidden />
        <span className="me-auto text-sm text-muted-foreground">
          {localizeServerMessage(t, MFG_MESSAGES.immutabilityStrip)}
        </span>
        <span className="text-sm font-semibold">
          {t('mfg.totalValue')}: <Amount value={data?.totalValue ?? '0.00'} />
        </span>
      </div>

      <Card>
        <CardContent className="overflow-x-auto">
          {isPending ? (
            <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />
          ) : isError ? (
            <p role="alert" className="py-8 text-center text-sm text-destructive">
              {t('common.loadFailed')}
            </p>
          ) : (data?.vouchers ?? []).length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              {localizeServerMessage(t, MFG_MESSAGES.noMatchingRows)}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('mfg.colRef')}</TableHead>
                  <TableHead>{t('mfg.colDateTime')}</TableHead>
                  <TableHead>{t('mfg.colWorkOrder')}</TableHead>
                  <TableHead>{t('mfg.colItem')}</TableHead>
                  <TableHead className="text-end">{t('mfg.colQty')}</TableHead>
                  <TableHead className="text-end">{t('mfg.colUnitCost')}</TableHead>
                  <TableHead className="text-end">{t('mfg.colValue')}</TableHead>
                  <TableHead>{t('mfg.colType')}</TableHead>
                  <TableHead>{t('mfg.colIssuedBy')}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.vouchers ?? []).flatMap((voucher) =>
                  voucher.lines.map((line, index) => (
                    <TableRow key={line.id} className="odd:bg-muted/30">
                      <TableCell>
                        {index === 0 ? (
                          <>
                            <Ltr value={voucher.code} className="font-mono text-sm" />
                            {/* Field 14's amber second line: `↩ MC-1003`.
                                NFR-04 — the return arrow on the ledger is one
                                of the rule's two named glyphs: dictionary
                                text, ↪ in Arabic, so it curls back along the
                                reading direction in both languages. */}
                            {voucher.correctsCode !== null ? (
                              <div className="text-xs text-amber-700 dark:text-amber-400">
                                {t('unit.corrects')} {voucher.correctsCode}
                              </div>
                            ) : null}
                          </>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {index === 0 ? (
                          <Ltr
                            value={voucher.issuedAt.replace('T', ' ').slice(0, 16)}
                            className="font-mono text-xs"
                          />
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {/* Field 16: an underlined LINK, blank when the
                            voucher has no job. */}
                        {index === 0 && voucher.workOrderId !== null ? (
                          <Link
                            to={`/manufacturing/work-orders/${voucher.workOrderId}`}
                            className="font-mono text-xs underline"
                          >
                            {voucher.workOrderCode}
                          </Link>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{line.itemName}</div>
                        <Ltr
                          value={line.itemCode}
                          className="font-mono text-xs text-muted-foreground"
                        />
                        {/* §5.5's [NOT IN PROTOTYPE] tracking field, built —
                            shown only for items that are tracked. */}
                        {line.trackingType === 'serial' && line.serialNos !== null ? (
                          <div className="text-xs text-muted-foreground">
                            {line.serialNos.join(', ')}
                          </div>
                        ) : null}
                        {line.trackingType === 'batch' && line.batchNo !== null ? (
                          <div className="text-xs text-muted-foreground">{line.batchNo}</div>
                        ) : null}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'text-end font-mono text-sm',
                          line.qty.startsWith('-')
                            ? 'text-emerald-700 dark:text-emerald-400'
                            : 'text-foreground',
                        )}
                      >
                        {/* `N-4` — one island, not a number and a loose
                            symbol: `units.symbol` is tenant free text and may
                            be Arabic script, and the bidi algorithm is free to
                            put it on the wrong side of its own figure. The
                            unit is this LINE's, the one the issue was written
                            in. */}
                        <Ltr
                          value={`${ledgerQty(line.qty, line.unitPrecision)} ${line.unitSymbol}`.trim()}
                          className="font-mono tabular-nums"
                        />
                      </TableCell>
                      <TableCell className="text-end">
                        <Amount value={line.unitCost} className="text-sm" />
                      </TableCell>
                      <TableCell className="text-end">
                        <Amount value={line.value} className="text-sm" />
                      </TableCell>
                      <TableCell>
                        {index === 0 ? (
                          <span className="rounded-full border px-2 py-0.5 text-xs">
                            {t(`mfg.issueType.${voucher.issueType}`)}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {/* EC-19-53 / §5.4's [recommended] Issued by column,
                            built — and the value is the SERVER's session
                            user, never free text a hand could mistype. */}
                        {index === 0 ? voucher.issuedBy : null}
                      </TableCell>
                      <TableCell>
                        {/* THE ONLY ROW ACTION. EC-19-52 / §7: it appears on
                            every row, including correction rows — a
                            correction of a correction is allowed, and the
                            `↩` back-references draw the whole chain — "a
                            correction chain is still a complete, readable
                            audit trail". */}
                        {index === 0 ? (
                          <Gated
                            permission="manufacturing.approve"
                            reasonKey="perm.cannot.correctConsumption"
                          >
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setForm({ correcting: voucher })}
                            >
                              <Undo2 aria-hidden /> {t('mfg.correct')}
                            </Button>
                          </Gated>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  )),
                )}
              </TableBody>
            </Table>
          )}

          {/* NFR-42 — the range, the total, and the two steps. The VOUCHER
              count, not the line count: a voucher is what the pager steps. */}
          {!isPending && !isError && (data?.total ?? 0) > 0 ? (
            <div className="pt-3">
              <Pager
                page={paging.page}
                pages={range.pages}
                first={range.first}
                last={range.last}
                total={range.total}
                onPage={paging.setPage}
                label={t('mfg.consumption')}
              />
            </div>
          ) : null}
        </CardContent>
      </Card>

      {form !== null ? (
        <VoucherForm correcting={form.correcting} onClose={() => setForm(null)} />
      ) : null}
    </div>
  );
}

/**
 * §5.5 — ONE FORM, reached from + New voucher and from Correct.
 *
 * "Clicking the dark background does not close this form. That is deliberate:
 * it is a data-entry form and it must be hard to lose by accident."
 */
function VoucherForm({
  correcting,
  onClose,
}: {
  correcting: ConsumptionVoucherResponse | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const post = usePostVoucher();
  const { data: warehouses } = useWarehouses();
  const [error, setError] = useState<string | null>(null);

  // The DATE is the company's (this voucher writes stock movements and posts);
  // the TIME beside it is a wall-clock time the user edits freely.
  const issueDate = useCompanyToday();
  const now = new Date();
  const [form, setForm] = useState({
    workOrderId: correcting?.workOrderId ?? '',
    warehouseId: correcting?.warehouseId ?? '',
    issuedAt: `${issueDate} ${now.toISOString().slice(11, 16)}`,
    issuedBy: '',
    // Correction mode forces Return-to-Stock; a fresh voucher defaults to
    // Planned, and §9 says the default is visible on the form and editable.
    issueType: (correcting === null ? 'planned' : 'return_to_stock') as IssueType,
    note: '',
  });
  const [lines, setLines] = useState<{ itemId: string; qty: string; serials: string }[]>([
    { itemId: correcting?.lines[0]?.itemId ?? '', qty: '', serials: '' },
  ]);

  /*
   * GEN-50 — the voucher's line picker. `stockable`: a service has no
   * quantity to issue to a job (BR-MD-03-07), and that narrowing is the
   * server's, because filtering a page of twenty afterwards is how a panel
   * silently shrinks.
   */
  const itemPicker = useItemPicker(
    lines.map((line) => line.itemId),
    { status: 'active', stockable: true },
  );
  /*
   * GEN-50 — field 23's job link. The HELD read is what makes this safe on the
   * correction door: `correcting.workOrderId` is a job somebody produced
   * weeks ago and has very likely closed, and a search-only picker would
   * render the field blank on a form whose whole purpose is to name it.
   */
  const workOrderPicker = useWorkOrderPicker(form.workOrderId === '' ? null : form.workOrderId);

  const trackingOf = (itemId: string) => itemPicker.byId.get(itemId)?.trackingType ?? 'none';

  // BR-MFG-04-20: required for every correction, and for Extra and Indirect —
  // "the two that need explaining".
  const noteRequired =
    correcting !== null || form.issueType === 'extra' || form.issueType === 'indirect';

  return (
    <Modal
      title={
        correcting === null
          ? t('mfg.newVoucher')
          : t('mfg.correctionOf', { code: correcting.code })
      }
      onClose={onClose}
      dismissible={false}
    >
      {correcting !== null ? (
        <p className="mb-4 rounded-md bg-amber-100 p-3 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-200">
          {localizeServerMessage(t, MFG_MESSAGES.correctionBanner)}
        </p>
      ) : null}
      {error !== null ? (
        <p role="alert" className="mb-3 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="grid gap-1.5">
          <Label htmlFor="mc-wo">{t('mfg.linkedWorkOrder')}</Label>
          {/* §5.5 field 23's own empty entry — `clearLabel` — is how an
              indirect issue is recorded. */}
          <Typeahead
            id="mc-wo"
            label={t('mfg.linkedWorkOrder')}
            value={form.workOrderId === '' ? null : form.workOrderId}
            disabled={correcting !== null}
            onChange={(id) => setForm({ ...form, workOrderId: id ?? '' })}
            options={workOrderPicker.options}
            selected={workOrderPicker.optionFor(form.workOrderId)}
            query={workOrderPicker.query}
            onQueryChange={workOrderPicker.setQuery}
            clearLabel={t('mfg.noWorkOrderIndirect')}
            placeholder={t('mfg.noWorkOrderIndirect')}
            emptyLabel={t('picker.noMatchingWorkOrder')}
            loading={workOrderPicker.loading}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="mc-warehouse">{t('mfg.warehouse')}</Label>
          <NativeSelect
            id="mc-warehouse"
            value={form.warehouseId}
            onChange={(event) => setForm({ ...form, warehouseId: event.target.value })}
          >
            <option value="">{t('common.select')}</option>
            {(warehouses ?? []).map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="grid gap-1.5">
          {/* Field 25: date AND time, as `YYYY-MM-DD HH:MM`. */}
          <Label htmlFor="mc-at">{t('mfg.dateTimeOfIssue')}</Label>
          <Input
            id="mc-at"
            className="font-mono"
            maxLength={16}
            value={form.issuedAt}
            onChange={(event) => setForm({ ...form, issuedAt: event.target.value })}
          />
        </div>
        <div className="grid gap-1.5">
          {/* Field 26: FREE TEXT, "so a storekeeper can post on someone
              else's behalf". */}
          <Label htmlFor="mc-by">{t('mfg.issuedBy')}</Label>
          <Input
            id="mc-by"
            value={form.issuedBy}
            onChange={(event) => setForm({ ...form, issuedBy: event.target.value })}
          />
        </div>
      </div>

      <div className="mt-4 grid gap-1.5">
        <Label>{t('mfg.issueType')}</Label>
        {/* Four segmented buttons, not a dropdown and not radio buttons. */}
        <div className="flex flex-wrap gap-2">
          {ISSUE_TYPES.map((type) => (
            <Button
              key={type}
              type="button"
              variant={form.issueType === type ? 'default' : 'outline'}
              disabled={correcting !== null}
              onClick={() => setForm({ ...form, issueType: type })}
            >
              {t(`mfg.issueType.${type}`)}
            </Button>
          ))}
        </div>
      </div>

      <div className="mt-4 grid gap-2">
        {lines.map((line, index) => (
          <div key={index} className="grid gap-2 sm:grid-cols-[2fr_1fr_2fr]">
            <ItemCell
              label={t('mfg.colItem')}
              clearLabel={t('common.select')}
              picker={itemPicker}
              value={line.itemId}
              onPick={(itemId) => {
                const next = [...lines];
                next[index] = { ...next[index]!, itemId };
                setLines(next);
              }}
            />
            <Input
              aria-label={t('mfg.colQty')}
              className="text-end font-mono"
              placeholder="0"
              value={line.qty}
              onChange={(event) => {
                const next = [...lines];
                next[index] = { ...next[index]!, qty: event.target.value };
                setLines(next);
              }}
            />
            {/* §5.5's [NOT IN PROTOTYPE] field, built and SHOWN ONLY FOR
                TRACKED ITEMS: "consuming a tracked component must capture
                which serials or which batch left the shelf". */}
            {trackingOf(line.itemId) !== 'none' ? (
              <Input
                aria-label={
                  trackingOf(line.itemId) === 'serial' ? t('mfg.serials') : t('mfg.batchNo')
                }
                placeholder={
                  trackingOf(line.itemId) === 'serial' ? t('mfg.serialsHint') : t('mfg.batchNo')
                }
                value={line.serials}
                onChange={(event) => {
                  const next = [...lines];
                  next[index] = { ...next[index]!, serials: event.target.value };
                  setLines(next);
                }}
              />
            ) : (
              <span />
            )}
          </div>
        ))}
        <div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLines([...lines, { itemId: '', qty: '', serials: '' }])}
          >
            <Plus aria-hidden /> {t('mfg.addItem')}
          </Button>
        </div>
      </div>

      <div className="mt-4 grid gap-1.5">
        <Label htmlFor="mc-note">
          {t('mfg.reasonNote')}
          {noteRequired ? <span className="text-destructive"> *</span> : null}
        </Label>
        <Textarea
          id="mc-note"
          rows={2}
          value={form.note}
          onChange={(event) => setForm({ ...form, note: event.target.value })}
        />
      </div>

      <div className="mt-5 flex justify-end gap-3">
        <Button variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        {/*
          EC-19-58 names this button by name: "the Post button on a consumption
          voucher… greyed by STYLING rather than really switched off. A button
          that looks switched off must be switched off." It is a real `disabled`
          attribute, so it cannot be clicked and a screen reader is told; the
          server refuses the same four conditions regardless (BR-MFG-04-04 /
          BR-MFG-04-20), because a disabled button is never a fence.
        */}
        <Button
          disabled={
            form.warehouseId === '' ||
            form.issuedBy.trim() === '' ||
            lines.every((line) => line.itemId === '' || line.qty === '') ||
            (noteRequired && form.note.trim() === '')
          }
          onClick={() => {
            setError(null);
            void post
              .mutateAsync({
                ...(form.workOrderId === '' ? {} : { workOrderId: form.workOrderId }),
                warehouseId: form.warehouseId,
                issuedAt: form.issuedAt,
                issuedBy: form.issuedBy,
                issueType: form.issueType,
                ...(form.note.trim() === '' ? {} : { note: form.note }),
                ...(correcting === null ? {} : { correctsVoucherId: correcting.id }),
                lines: lines
                  .filter((line) => line.itemId !== '' && line.qty !== '')
                  .map((line) => {
                    const tracking = trackingOf(line.itemId);
                    const serials = line.serials
                      .split(',')
                      .map((entry) => entry.trim())
                      .filter((entry) => entry !== '');
                    return {
                      itemId: line.itemId,
                      qty: line.qty,
                      ...(tracking === 'serial' && serials.length > 0 ? { serialNos: serials } : {}),
                      ...(tracking === 'batch' && line.serials.trim() !== ''
                        ? { batchNo: line.serials.trim() }
                        : {}),
                    };
                  }),
              })
              .then(onClose)
              .catch((caught: unknown) => setError(refusalMessage(caught)));
          }}
        >
          {/* Field 35: the verb is POST, not Save — "accounting language for an
              irreversible append". */}
          {t('mfg.postVoucher')}
        </Button>
      </div>
    </Modal>
  );
}

/** §5.6 — one card per work order. Reading and drilling are all it does. */
function VarianceTab() {
  const { t } = useTranslation();
  const { data: cards, isPending } = useConsumptionVariance();

  if (isPending) return <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />;
  if ((cards ?? []).length === 0) {
    return <p className="py-12 text-center text-sm text-muted-foreground">{t('mfg.noVariance')}</p>;
  }

  return (
    <div className="grid gap-4">
      {(cards ?? []).map((card) => (
        <Card key={card.workOrderId}>
          <CardHeader>
            <CardTitle>
              {t('mfg.varianceFor')} <Ltr value={card.workOrderCode} className="font-mono" />
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('mfg.colItem')}</TableHead>
                  <TableHead className="text-end">{t('mfg.colPlanned')}</TableHead>
                  <TableHead className="text-end">{t('mfg.colActual')}</TableHead>
                  <TableHead className="text-end">{t('mfg.variance')}</TableHead>
                  <TableHead className="text-end">{t('mfg.variancePct')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {card.rows.map((row) => (
                  <TableRow key={row.itemId} className="odd:bg-muted/30">
                    <TableCell>
                      <div className="text-sm">{row.itemName}</div>
                      <Ltr
                        value={row.itemCode}
                        className="font-mono text-xs text-muted-foreground"
                      />
                    </TableCell>
                    <TableCell className="text-end font-mono text-sm">
                      {row.plannedQty}
                    </TableCell>
                    <TableCell className="text-end font-mono text-sm">{row.actualQty}</TableCell>
                    <TableCell
                      className={cn('text-end font-mono text-sm', varianceBandClass(row.band))}
                    >
                      {row.varianceQty}
                    </TableCell>
                    <TableCell
                      className={cn('text-end font-mono text-sm', varianceBandClass(row.band))}
                    >
                      {/* BR-MFG-04-13: a planned quantity of zero shows `—`
                          and is not clickable. Division by zero never reaches
                          the screen.

                          ERR-MFG-04-09 is the same behaviour read from the
                          user's side, and its §14 row is one of the few that
                          prescribes doing NOTHING: "Clicked a variance
                          percentage on a row planned at zero | The percentage
                          shows `—` and does not respond | Nothing — there is
                          no planned figure to compare against." So the dash
                          is the answer, not a placeholder waiting for one. */}
                      {row.variancePct === null ? '—' : <Pct value={row.variancePct} />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}
      {/* §5.6's hint line, worded exactly. */}
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <Info className="size-3.5" aria-hidden /> {localizeServerMessage(t, MFG_MESSAGES.varianceHint)}
      </p>
    </div>
  );
}

/** §5.7 — grouped by item and by nothing else, over its OWN date range. */
function UsageTab() {
  const { t } = useTranslation();
  const today = useCompanyToday();
  const [range, setRange] = useState({ from: `${today.slice(0, 8)}01`, to: today });
  const { data, isPending } = useUsageReport(range.from, range.to);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <span className="me-auto">{t('mfg.usageReport')}</span>
          <Input
            aria-label={t('common.from')}
            type="date"
            className="w-40"
            value={range.from}
            onChange={(event) => setRange({ ...range, from: event.target.value })}
          />
          {/* NFR-04 — the usage screen's right arrow, the rule's other named
              glyph: dictionary text, ← in Arabic (the from-field sits to the
              RIGHT of the to-field once the row mirrors). */}
          <span aria-hidden>{t('unit.arrow')}</span>
          <Input
            aria-label={t('common.to')}
            type="date"
            className="w-40"
            value={range.to}
            onChange={(event) => setRange({ ...range, to: event.target.value })}
          />
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3">
        {isPending ? (
          <div className="h-32 animate-pulse rounded-md bg-muted" aria-busy />
        ) : (data?.rows ?? []).length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{t('mfg.noUsage')}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('mfg.colItem')}</TableHead>
                <TableHead className="text-end">{t('mfg.colTotalConsumed')}</TableHead>
                <TableHead className="text-end">{t('mfg.colValue')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.rows ?? []).map((row) => (
                <TableRow key={row.itemId} className="odd:bg-muted/30">
                  <TableCell>
                    <div className="text-sm">{row.itemName}</div>
                    <Ltr value={row.itemCode} className="font-mono text-xs text-muted-foreground" />
                  </TableCell>
                  {/* §5.7's total is grouped BY ITEM, so it has exactly one
                      unit and `N-14`'s "never added together" — which forbids
                      a sum ACROSS items — does not reach it. The symbol was
                      already here; the decimals were not, and the pair was two
                      loose text nodes rather than one bidi island. */}
                  <TableCell className="text-end text-sm">
                    <Qty
                      value={row.totalQty}
                      precision={row.unitPrecision}
                      unit={row.unitSymbol}
                    />
                  </TableCell>
                  <TableCell className="text-end">
                    <Amount value={row.value} className="text-sm" />
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={2} className="text-end text-sm font-semibold">
                  {t('mfg.totalValue')}
                </TableCell>
                <TableCell className="text-end">
                  <Amount value={data?.totalValue ?? '0.00'} className="font-semibold" />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
        {/* §5.7's footer line, worded exactly — the design file names this
            connection outright, so it is evidence rather than a guess. */}
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Zap className="size-3.5" aria-hidden />{' '}
          {localizeServerMessage(t, MFG_MESSAGES.usageFeedsMrp)}
        </p>
      </CardContent>
    </Card>
  );
}
